import { ApiError, apiFetch, type AuthState } from "@/lib/auth";
import type { SessionTerminal } from "@/lib/terminal-prefs";

export type SessionState = "active" | "detached";

export type SessionOut = {
  session_id: string;
  connection_id: string;
  name: string;
  type: string;
  tenant_id: string;
  tenant_color: string;
  state: SessionState;
  started_at: string;
};

export type SessionCreated = {
  session_id: string;
  ticket: string;
  expires_in: number;
  connection: { id: string; name: string; type: string };
};

export type CloseTabAction = "close" | "detach";

export async function openSession(
  connectionId: string,
  resume?: string,
  terminal?: SessionTerminal,
  connectorId?: string,
): Promise<SessionCreated> {
  return apiFetch<SessionCreated>("/api/sessions", {
    method: "POST",
    body: JSON.stringify({
      connection_id: connectionId,
      ...(resume ? { resume } : {}),
      ...(terminal ? { terminal } : {}),
      ...(connectorId ? { connector_id: connectorId } : {}),
    }),
  });
}

export function listSessions(): Promise<SessionOut[]> {
  return apiFetch<SessionOut[]>("/api/sessions");
}

export async function closeSession(sessionId: string): Promise<void> {
  await apiFetch<undefined>(`/api/sessions/${sessionId}`, { method: "DELETE" });
}

export async function patchMe(closeTabAction: CloseTabAction): Promise<void> {
  await apiFetch("/api/auth/users/me", {
    method: "PATCH",
    body: JSON.stringify({ close_tab_action: closeTabAction }),
  });
}

export type MyProfile = {
  name: string | null;
  email: string;
  close_tab_action: CloseTabAction;
};

/** Caller identity (change redesign-app-panels): feeds Perfil > Identidad. */
export function getMyProfile(): Promise<MyProfile> {
  return apiFetch("/api/auth/me");
}

/** Update the display name (change redesign-app-panels). */
export function patchMyName(name: string): Promise<MyProfile> {
  return apiFetch("/api/auth/users/me", {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export type TenantPatch = {
  name?: string;
  color?: string;
  max_sessions_per_user?: number | null;
  detached_session_ttl?: number;
  /** Desktop console enablement (add-desktop-console), owner/admin only. */
  desktop_enabled?: boolean;
};

export async function patchTenant(
  tenantId: string,
  patch: TenantPatch,
): Promise<Record<string, unknown>> {
  return apiFetch(`/api/auth/tenants/${tenantId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** Owner-only delete; `confirmName` must match the current tenant name. */
export function deleteTenant(
  tenantId: string,
  confirmName: string,
): Promise<AuthState> {
  return apiFetch<AuthState>(`/api/auth/tenants/${tenantId}`, {
    method: "DELETE",
    body: JSON.stringify({ confirm_name: confirmName }),
  });
}

export type TenantMember = {
  user_id: string;
  email: string;
  name?: string | null;
  role: string;
};

export function listTenantMembers(tenantId: string): Promise<{ members: TenantMember[] }> {
  return apiFetch(`/api/auth/tenants/${tenantId}/members`);
}

export async function changeMemberRole(
  tenantId: string,
  userId: string,
  role: string,
): Promise<void> {
  await apiFetch(`/api/auth/tenants/${tenantId}/members/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export type TenantInvitation = {
  email: string;
  role: string;
  token: string;
  invited_at?: number | null;
  invited_by?: string | null;
};

export type InviteResult = {
  status: string;
  email_sent: boolean;
  invite_url: string | null;
};

export function listTenantInvitations(tenantId: string): Promise<{ invitations: TenantInvitation[] }> {
  return apiFetch(`/api/auth/tenants/${tenantId}/invitations`);
}

export type TenantTeam = { id: string; name: string };

export function listTenantTeams(tenantId: string): Promise<{ teams: TenantTeam[] }> {
  return apiFetch(`/api/auth/tenants/${tenantId}/teams`);
}

export async function inviteMember(
  tenantId: string,
  teamId: string,
  email: string,
  role: string,
  locale?: string,
): Promise<InviteResult> {
  return apiFetch<InviteResult>("/api/invitations", {
    method: "POST",
    body: JSON.stringify({ tenant_id: tenantId, team_id: teamId, email, role, locale }),
  });
}

export function resendInvite(token: string): Promise<InviteResult> {
  return apiFetch<InviteResult>(`/api/invitations/${encodeURIComponent(token)}/resend`, {
    method: "POST",
  });
}

export async function revokeInvite(token: string): Promise<void> {
  await apiFetch(`/api/invitations/${encodeURIComponent(token)}`, { method: "DELETE" });
}

export { ApiError };
